/**
 * Shortens an ID/HWID string to just leading and trailing characters.
 * Unicode symbol alternatives: ‥,…,~,≈,-,•,◇
 */
export const shortenId = (id: string, numChars = 4) => {
    if (typeof id !== 'string') throw new Error(`id is not a string`);
    if (typeof numChars !== 'number' || numChars < 3) throw new Error(`numChars must be a number >= 3`);

    const [idType, idValue] = id.split(':', 2);
    if (!idType || !idValue) {
        return id; // Invalid format, return as is
    }

    const threshold = numChars * 2 + 2;
    if (idValue.length <= threshold) {
        return id; // Do not shorten if ID value is threshold characters or fewer
    }

    const start = idValue.slice(0, numChars);
    const end = idValue.slice(-numChars);
    return `${idType}:${start}…${end}`;
}


const discordIdPrefix = 'discord:';
const ucpBaseUrl = 'https://ucp.finalrp.de';
const acpBaseUrl = 'https://acp.finalrp.de';

/**
 * Returns the newest action of a given type that is neither revoked nor expired.
 * Mirrors what the backend considers active, so both agree on the same entry.
 */
export const findActivePlayerAction = <T extends {
    type: string;
    exp?: number;
    revokedAt?: number;
}>(actionHistory: T[], type: string, serverTime: number): T | undefined => {
    const matches = actionHistory.filter((a) => (
        a.type === type
        && !a.revokedAt
        && (typeof a.exp !== 'number' || a.exp > serverTime)
    ));
    return matches[matches.length - 1];
}

/**
 * Returns the discord id out of the first identifier list containing one.
 */
export const findDiscordId = (...idLists: (string[] | undefined)[]) => {
    for (const ids of idLists) {
        const found = ids?.find((id) => id.startsWith(discordIdPrefix));
        if (found) return found.slice(discordIdPrefix.length);
    }
}

/**
 * Returns the UCP analytics page url of a given discord id.
 */
//display-only data pushed by the server core resource - known keys get a nicer
//label, everything else falls back to the key itself
const coreDataLabels: Record<string, string> = {
    name: 'Core Name',
    job: 'Job',
};
const coreDataOrder = ['name', 'job'];
//keys that never get a row of their own
const coreDataSkipped = [
    'group', //shown in the player modal title tooltip instead
    'jobGrade', //appended to the job row below
    'dead', //only used to force the revive option in the heal dialog
];

/**
 * The core reports its own death state, which is not the same as the ped being dead.
 */
export const isCoreDead = (coreData?: Record<string, string>) => coreData?.dead === 'true';

export type CoreDataEntryType = { key: string; label: string; value: string };

export const getCoreDataEntries = (coreData?: Record<string, string>): CoreDataEntryType[] => {
    if (!coreData) return [];
    const rank = (key: string) => {
        const index = coreDataOrder.indexOf(key);
        return index === -1 ? coreDataOrder.length : index;
    };
    return Object.entries(coreData)
        .filter(([key]) => !coreDataSkipped.includes(key))
        .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
        .map(([key, value]) => ({
            key,
            label: coreDataLabels[key]
                ?? key.charAt(0).toUpperCase() + key.slice(1).replace(/[_-]+/g, ' '),
            value: key === 'job' && coreData.jobGrade
                ? `${value} - ${coreData.jobGrade}`
                : value,
        }));
}

export const getUcpAnalyticsUrl = (discordId: string) => {
    return `${ucpBaseUrl}/analytics/${discordId}`;
}

/**
 * Returns the UCP network page url of a given discord id.
 */
export const getUcpNetworkUrl = (discordId: string) => {
    return `${ucpBaseUrl}/analytics/network/${discordId}`;
}

/**
 * Returns the ACP page url of a given discord id.
 */
export const getAcpUrl = (discordId: string) => {
    return `${acpBaseUrl}/auth/entities/${discordId}`;
}

/**
 * Sorts identifiers alphabetically, with the license ones pinned to the top
 * because they are the ones an admin looks for first.
 */
export const sortPlayerIds = (a: string, b: string) => {
    const getPriority = (id: string) => {
        if (id.startsWith('license:')) return 0;
        if (id.startsWith('license2:')) return 1;
        return 2;
    };

    const aPriority = getPriority(a);
    const bPriority = getPriority(b);
    if (aPriority === bPriority) {
        return a.localeCompare(b);
    }
    return aPriority - bPriority;
};


/**
 * Sorts sanction templates so the normal ones come first and the flagged ones
 * (anticheat sanctions, analyse warns) are grouped at the end.
 */
export const sortSanctionTemplates = <T extends { anticheat?: boolean, analyse?: boolean }>(templates: T[]): T[] => {
    const isFlagged = (tpl: T) => !!tpl.anticheat || !!tpl.analyse;
    return templates
        .slice()
        .sort((a, b) => Number(isFlagged(a)) - Number(isFlagged(b)));
};
